import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Button,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Divider,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import "jspdf-autotable";
import {
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  DirectionsCar as CarIcon,
  MonetizationOn as GoldIcon,
  Percent as PercentIcon,
  ReceiptLong as ReceiptIcon,
  ArrowForward as ArrowForwardIcon,
  Visibility as VisibilityIcon,
  Tune as TuneIcon,
} from "@mui/icons-material";
import ResponsiveAppBar from "../components/AppBar";
import DeleteWithPassword from "../components/deleteWithPassword";
import RoleGuard from "../components/RoleGuard";
import { API_BASE_URL } from "../api/config";

// Create theme with specified color scheme
const theme = createTheme({
  palette: {
    primary: { main: '#77c8c7' }, // Teal
    secondary: { main: '#936097' }, // Plum
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    success: { main: '#77c8c7' },
    info: { main: '#936097' },
  },
  typography: {
    fontFamily: '"Outfit", "Poppins", "Roboto", sans-serif',
    h4: {
      fontWeight: 800,
      letterSpacing: '-0.5px',
      color: '#1e293b',
    },
    h5: {
      fontWeight: 700,
      letterSpacing: '-0.3px',
      color: '#1e293b',
    },
    subtitle1: {
      fontWeight: 500,
      color: '#64748b',
      fontSize: '1.05rem',
    },
    button: {
      fontWeight: 700,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 22px',
          boxShadow: '0 4px 12px rgba(147, 96, 151, 0.08)',
          transition: 'all 0.25s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 20px rgba(119, 200, 199, 0.25)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

const BASE_URL = API_BASE_URL;

const AdminPage = () => {
  const [loans, setLoans] = useState([]);
  const [vehicleLoans, setVehicleLoans] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState("gold");
  const [goldPricePerGram, setGoldPricePerGram] = useState(0);
  const [interestRate, setInterestRate] = useState(0);
  const [adminName, setAdminName] = useState("Selvarasan");
  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPage, setSelectedPage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchLoans();
    fetchVehicleLoans();
    fetchSettings();
    const user = JSON.parse(sessionStorage.getItem("user"));
    if (user && user.name) {
      setAdminName(user.name);
    }
  }, []);

  const fetchLoans = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/gold-loan/get_all_goldloan`);
      setLoans(response.data || []);
    } catch (error) {
      console.error("Error fetching loans:", error);
    }
  };

  const fetchVehicleLoans = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/vehicle-loan/get_all_loans`);
      setVehicleLoans(response.data || []);
    } catch (error) {
      console.error("Error fetching vehicle loans:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/admin/fetch_settings`);
      setGoldPricePerGram(response.data.goldPricePerGram || 0);
      setInterestRate(response.data.interestRate || 0);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleSettingsUpdate = async () => {
    try {
      await axios.put(`${BASE_URL}/admin/update_settings`, {
        goldPricePerGram,
        interestRate,
      });
      setOpenSettingsDialog(false);
    } catch (error) {
      console.error("Error updating settings:", error);
    }
  };

  const handleSettingsClick = () => {
    setOpenSettingsDialog(true);
  };

  const handleCloseSettingsDialog = () => {
    setOpenSettingsDialog(false);
  };

  const handleNavigate = (event) => {
    const page = event.target.value;
    setSelectedPage(page);
    if (page) {
      navigate(page);
    }
  };

  const handleLoanTypeChange = (event, newLoanType) => {
    if (newLoanType) setSelectedLoan(newLoanType);
  };

  const handleNewUserClick = () => {
    navigate("/add-user");
  };

  const handleDeleteLoan = async (loanId) => {
    if (selectedLoan === "gold") {
      await axios.delete(`${BASE_URL}/gold-loan/delete_goldLoan_by_id/${loanId}`);
      fetchLoans();
    } else {
      await axios.delete(`${BASE_URL}/vehicle-loan/delete_vehicleloan_by_id/${loanId}`);
      fetchVehicleLoans();
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setOpenImageDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenImageDialog(false);
    setSelectedImage(null);
  };

  const currentList = selectedLoan === "gold" ? loans : vehicleLoans;

  return (
    <>
      <ResponsiveAppBar />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 3, md: 5 } }}>
          <Container maxWidth="lg">
            {/* Header Title Section */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
              <Box>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.6, borderRadius: '50px', bgcolor: 'rgba(119, 200, 199, 0.15)', mb: 1.5 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#77c8c7' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#936097', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Admin Control Center
                  </Typography>
                </Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
                  Loan Applications & Operations
                </Typography>
                <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
                  Welcome back, <strong style={{ color: '#936097' }}>{adminName}</strong> • Manage customer requests & live rates
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <RoleGuard allowedRoles={["Admin Manager", "Branch Admin"]}>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={handleNewUserClick}
                    sx={{
                      background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                      color: '#ffffff',
                      fontWeight: 700,
                    }}
                  >
                    Add User
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={handleSettingsClick}
                    sx={{
                      borderColor: '#936097',
                      color: '#936097',
                      fontWeight: 700,
                      bgcolor: '#ffffff',
                      '&:hover': {
                        borderColor: '#77c8c7',
                        bgcolor: 'rgba(119, 200, 199, 0.08)',
                      }
                    }}
                  >
                    Gold Rate Settings
                  </Button>
                </RoleGuard>
              </Box>
            </Box>

            {/* KPI Summary Cards */}
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(119, 200, 199, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(119, 200, 199, 0.15)' } }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 50, height: 50, borderRadius: 3, bgcolor: 'rgba(119, 200, 199, 0.15)', color: '#77c8c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <GoldIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Gold Loans
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        {loans.length} <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#64748b' }}>applied</span>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(147, 96, 151, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(147, 96, 151, 0.15)' } }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 50, height: 50, borderRadius: 3, bgcolor: 'rgba(147, 96, 151, 0.12)', color: '#936097', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CarIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Vehicle Loans
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        {vehicleLoans.length} <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#64748b' }}>applied</span>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(119, 200, 199, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(119, 200, 199, 0.15)' } }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 50, height: 50, borderRadius: 3, bgcolor: 'rgba(119, 200, 199, 0.15)', color: '#77c8c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <TuneIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Gold Rate / Gram
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        ₹{Number(goldPricePerGram).toLocaleString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ borderRadius: 3.5, bgcolor: '#ffffff', border: '1px solid rgba(147, 96, 151, 0.3)', boxShadow: '0 8px 24px rgba(0,0,0,0.03)', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(147, 96, 151, 0.15)' } }}>
                  <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 50, height: 50, borderRadius: 3, bgcolor: 'rgba(147, 96, 151, 0.12)', color: '#936097', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PercentIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Interest Rate
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                        {interestRate}% <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#64748b' }}>p.a.</span>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Controls Bar: Toggle & Navigation */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3.5,
                borderRadius: 3.5,
                bgcolor: '#ffffff',
                border: '1px solid rgba(226, 232, 240, 0.9)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <ToggleButtonGroup
                value={selectedLoan}
                exclusive
                onChange={handleLoanTypeChange}
                aria-label="loan type selection"
                sx={{
                  bgcolor: '#f1f5f9',
                  p: 0.5,
                  borderRadius: 3,
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: 2.5,
                    px: 3,
                    py: 1,
                    fontWeight: 700,
                    color: '#64748b',
                    display: 'flex',
                    gap: 1,
                    transition: 'all 0.2s ease',
                    '&.Mui-selected': {
                      bgcolor: '#ffffff',
                      color: '#936097',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      '&:hover': {
                        bgcolor: '#ffffff',
                      },
                    },
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.6)',
                    }
                  }
                }}
              >
                <ToggleButton value="gold">
                  <GoldIcon sx={{ fontSize: 18, color: selectedLoan === 'gold' ? '#77c8c7' : 'inherit' }} />
                  Gold Loans ({loans.length})
                </ToggleButton>
                <ToggleButton value="vehicle">
                  <CarIcon sx={{ fontSize: 18, color: selectedLoan === 'vehicle' ? '#936097' : 'inherit' }} />
                  Vehicle Loans ({vehicleLoans.length})
                </ToggleButton>
              </ToggleButtonGroup>

              <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/loan-details")}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    borderColor: 'rgba(119, 200, 199, 0.6)',
                    color: '#1e293b',
                    bgcolor: '#f8fafc',
                    '&:hover': {
                      borderColor: '#77c8c7',
                      bgcolor: '#ffffff',
                    }
                  }}
                >
                  Full DataGrid View
                </Button>

                <FormControl size="small" sx={{ minWidth: 200, flex: { xs: 1, sm: 'none' } }}>
                  <InputLabel sx={{ fontWeight: 500, color: '#64748b' }}>Navigate Module</InputLabel>
                  <Select
                    value={selectedPage}
                    label="Navigate Module"
                    onChange={handleNavigate}
                    sx={{
                      borderRadius: 2.5,
                      bgcolor: '#f8fafc',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(226, 232, 240, 0.9)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#77c8c7',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#936097',
                      }
                    }}
                  >
                    <MenuItem value="/admin">Gold & Vehicle Loan</MenuItem>
                    <MenuItem value="/velavan-finance">Velavan Finance Ledger</MenuItem>
                    <MenuItem value="/due-matters">Due Matters</MenuItem>
                    <MenuItem value="/interestCalculation">Interest Calculation</MenuItem>
                    <MenuItem value="/dailyAccounts">Daily Accounts</MenuItem>
                    <MenuItem value="/finalAccounts">Final Accounts</MenuItem>
                    <MenuItem value="/monthly-graph">Monthly Graph Status</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Paper>

            {/* Applications Table */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                bgcolor: '#ffffff',
                border: '1px solid rgba(226, 232, 240, 0.9)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04)',
              }}
            >
              <Box sx={{ p: 2.5, px: 3, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#ffffff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: 2.5, bgcolor: selectedLoan === 'gold' ? 'rgba(119, 200, 199, 0.15)' : 'rgba(147, 96, 151, 0.12)', color: selectedLoan === 'gold' ? '#77c8c7' : '#936097', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {selectedLoan === 'gold' ? <GoldIcon /> : <CarIcon />}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                      {selectedLoan === "gold" ? "Gold Loan Applications" : "Vehicle Loan Applications"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Showing {currentList.length} total applicant records
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={selectedLoan === 'gold' ? 'Active Gold Scheme' : 'Active Vehicle Scheme'}
                  sx={{
                    bgcolor: selectedLoan === 'gold' ? 'rgba(119, 200, 199, 0.15)' : 'rgba(147, 96, 151, 0.12)',
                    color: selectedLoan === 'gold' ? '#319795' : '#936097',
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                />
              </Box>

              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, color: '#475569', py: 2 }}>Applicant Name</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, color: '#475569', py: 2 }}>Email Address</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, color: '#475569', py: 2 }}>Contact Phone</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, color: '#475569', py: 2 }}>{selectedLoan === "gold" ? "Gold Weight (g)" : "Vehicle Model"}</TableCell>
                      {selectedLoan === "vehicle" && (
                        <>
                          <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, color: '#475569', py: 2 }}>Loan Amount</TableCell>
                          <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, color: '#475569', py: 2 }}>Vehicle Photo</TableCell>
                        </>
                      )}
                      <TableCell align="right" sx={{ bgcolor: '#f8fafc', fontWeight: 700, color: '#475569', py: 2 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={selectedLoan === "vehicle" ? 7 : 5} sx={{ textAlign: 'center', py: 8 }}>
                          <ReceiptIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                          <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 600 }}>
                            No Applications Found
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                            New loan submissions will automatically populate this table.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      currentList.map((loan) => (
                        <TableRow
                          key={loan._id}
                          hover
                          sx={{
                            '&:hover': { bgcolor: 'rgba(119, 200, 199, 0.04)' },
                            transition: 'background-color 0.15s',
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {loan.borrowerName || loan.fullName || "N/A"}
                          </TableCell>
                          <TableCell sx={{ color: '#475569' }}>{loan.email || "N/A"}</TableCell>
                          <TableCell sx={{ color: '#475569' }}>{loan.phoneNumber || "N/A"}</TableCell>
                          <TableCell>
                            <Chip
                              label={selectedLoan === "gold" ? `${loan.goldWeight || 0} grams` : (loan.vehicleModel || "N/A")}
                              size="small"
                              sx={{
                                bgcolor: selectedLoan === 'gold' ? 'rgba(119, 200, 199, 0.12)' : 'rgba(147, 96, 151, 0.1)',
                                color: selectedLoan === 'gold' ? '#0f766e' : '#936097',
                                fontWeight: 700,
                              }}
                            />
                          </TableCell>
                          {selectedLoan === "vehicle" && (
                            <>
                              <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>
                                ₹{loan.loanAmount ? Number(loan.loanAmount).toLocaleString() : "N/A"}
                              </TableCell>
                              <TableCell>
                                {loan.vehicleImageUrl ? (
                                  <Box
                                    component="img"
                                    src={loan.vehicleImageUrl}
                                    alt={`Vehicle of ${loan.fullName}`}
                                    sx={{
                                      width: 60,
                                      height: 40,
                                      objectFit: 'cover',
                                      borderRadius: 1.5,
                                      cursor: 'pointer',
                                      border: '1px solid rgba(119, 200, 199, 0.4)',
                                      transition: 'transform 0.2s',
                                      '&:hover': {
                                        transform: 'scale(1.1)',
                                      }
                                    }}
                                    onClick={() => handleImageClick(loan.vehicleImageUrl)}
                                  />
                                ) : (
                                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>None</Typography>
                                )}
                              </TableCell>
                            </>
                          )}
                          <TableCell align="right">
                            <RoleGuard allowedRoles={["Admin Manager"]}>
                              <DeleteWithPassword
                                resourceId={loan._id}
                                resourceName="Loan"
                                onDelete={handleDeleteLoan}
                                navigate={navigate}
                              />
                            </RoleGuard>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Container>
        </Box>

        {/* Vehicle Image Modal */}
        <Dialog
          open={openImageDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3.5, overflow: 'hidden' }
          }}
        >
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)', color: '#ffffff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CarIcon /> Vehicle Photo Preview
          </DialogTitle>
          <DialogContent sx={{ p: 3, bgcolor: '#f8fafc', display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              src={selectedImage}
              alt="Selected Vehicle"
              sx={{ maxWidth: "100%", maxHeight: 500, objectFit: 'contain', borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: '#ffffff' }}>
            <Button onClick={handleCloseDialog} sx={{ color: '#936097', fontWeight: 700 }}>
              Close Preview
            </Button>
          </DialogActions>
        </Dialog>

        {/* Gold Settings Dialog */}
        <Dialog
          open={openSettingsDialog}
          onClose={handleCloseSettingsDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3.5, overflow: 'hidden' }
          }}
        >
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)', color: '#ffffff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TuneIcon /> Update Gold Loan Rates
          </DialogTitle>
          <DialogContent sx={{ pt: 3.5, pb: 2, bgcolor: '#ffffff' }}>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
              Adjust live benchmark rates. All new calculations will immediately use these parameters.
            </Typography>
            <TextField
              label="Gold Price per Gram (₹)"
              type="number"
              fullWidth
              variant="outlined"
              value={goldPricePerGram}
              onChange={(e) => setGoldPricePerGram(e.target.value)}
              sx={{
                mb: 2.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#77c8c7',
                  }
                }
              }}
            />
            <TextField
              label="Annual Interest Rate (%)"
              type="number"
              fullWidth
              variant="outlined"
              value={interestRate}
              onChange={(e) => setInterestRate(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#936097',
                  }
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
            <Button
              onClick={handleCloseSettingsDialog}
              sx={{ color: '#64748b', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSettingsUpdate}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #77c8c7 0%, #936097 100%)',
                color: '#ffffff',
                fontWeight: 700,
                px: 3,
              }}
            >
              Save Parameters
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </>
  );
};

export default AdminPage;